import { useState, useMemo } from 'react';

import {
  Button,
  Input,
  Badge,
  Avatar,
  EmptyState,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Card,
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
  DialogFooter,
  PageHeader,
  useToast,
} from '@/components/ui';
import { SearchIcon, ClientesIcon } from '@/components/icons';
import { useUsers, useUserActions } from '@/lib/admin-store';
import { useDepartments } from '@/lib/admin-store';
import { ApiError } from '@/lib/api';
import type { User } from '@/lib/admin-types';

import { UserFormDialog } from './UserFormDialog';

import { formatDate } from '@/lib/format-date';
/**
 * Tela admin: Usuários.
 *
 * Lista todos os usuários com filtro por nome/email, ação de criar novo,
 * ação de editar (modal) e excluir (confirmação).
 *
 * Estado vem do admin-store (mock em memória). Trocar por React Query na
 * Frente A sem alterar a UI.
 */
export function UsersPage() {
  const users = useUsers();
  const departments = useDepartments();
  const { deleteUser, pending } = useUserActions();
  const { show } = useToast();

  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<User | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) => u.nome.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    );
  }, [users, search]);

  const departmentLabel = (slug: string) =>
    departments.find((d) => d.slug === slug)?.nome || slug;

  async function handleConfirmDelete() {
    if (!confirmDelete) return;
    try {
      await deleteUser(confirmDelete.id);
    } catch (err) {
      // C3 (rodada AJUDA-UX-QA): usuário com histórico não pode ser excluído
      // (o servidor devolve 409). O caminho certo é INATIVAR — nada é apagado.
      if (err instanceof ApiError && err.status === 409) {
        show(
          `${confirmDelete.nome} tem histórico no sistema e não pode ser excluído. Use "Editar" e desligue "Usuário ativo" — o acesso é bloqueado e nada é apagado.`,
          { variant: 'error', durationMs: 6000 },
        );
      } else {
        show(
          err instanceof Error ? err.message : 'Não foi possível excluir o usuário.',
          { variant: 'error' },
        );
      }
      setConfirmDelete(null);
      return;
    }
    setConfirmDelete(null);
  }

  const ativos = users.filter((u) => u.ativo).length;

  return (
    <section className="min-w-0">
      <PageHeader
        crumb="Configurações · Pessoas"
        title="Usuários e acessos"
        subtitle={
          users.length === 0
            ? 'Quem pode entrar no sistema e com quais departamentos.'
            : `${users.length} ${users.length === 1 ? 'pessoa cadastrada' : 'pessoas cadastradas'} · ${ativos} com acesso ativo.`
        }
        actions={<Button onClick={() => setCreating(true)}>Novo usuário</Button>}
      />

      {/* Busca */}
      <div className="mb-4 w-full max-w-md">
        <Input
          placeholder="Buscar por nome ou e-mail…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftIcon={<SearchIcon className="w-4 h-4" />}
        />
      </div>

      {/* Lista */}
      {filtered.length === 0 ? (
        <Card variant="flat">
          <EmptyState
            icon={<ClientesIcon className="w-6 h-6" />}
            title={search ? 'Nenhum usuário encontrado' : 'Nenhum usuário cadastrado'}
            description={
              search
                ? `Não encontramos nenhum usuário correspondendo a "${search}".`
                : 'Adicione o primeiro usuário da plataforma pra começar.'
            }
            action={
              !search && <Button onClick={() => setCreating(true)}>Adicionar usuário</Button>
            }
          />
        </Card>
      ) : (
        <Card variant="flat" className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Usuário</TableHead>
                <TableHead>Departamentos</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Último acesso</TableHead>
                <TableHead align="right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar name={u.nome} src={u.fotoUrl} size="sm" />
                      <div className="min-w-0">
                        <div className="font-medium text-ink-1 dark:text-ink-dark-1">
                          {u.nome}
                          {u.isAdmin && (
                            <Badge variant="info" size="sm" className="ml-2">
                              Admin
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-ink-3 dark:text-ink-dark-3 truncate">
                          {u.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {u.departamentos.length === 0 ? (
                      <span className="text-xs text-ink-3 dark:text-ink-dark-3 italic">
                        Sem departamento
                      </span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {u.departamentos.slice(0, 3).map((dr) => (
                          <Badge key={dr.departmentSlug} variant="neutral" size="sm">
                            {departmentLabel(dr.departmentSlug)}
                          </Badge>
                        ))}
                        {u.departamentos.length > 3 && (
                          <Badge variant="neutral" size="sm">
                            +{u.departamentos.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.ativo ? (
                      <Badge variant="success" dot>
                        Ativo
                      </Badge>
                    ) : (
                      <Badge variant="neutral" dot>
                        Inativo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-ink-2 dark:text-ink-dark-2">
                      {formatRelativeTime(u.ultimoAcessoIso)}
                    </span>
                  </TableCell>
                  <TableCell align="right">
                    <div className="flex flex-wrap items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(u)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(u)}
                        className="text-red hover:bg-red-soft dark:hover:bg-red-soft-dark"
                      >
                        Excluir
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Modal criar/editar */}
      {(creating || editing) && (
        <UserFormDialog
          open
          user={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}

      {/* Confirmação de exclusão */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        size="sm"
      >
        {confirmDelete && (
          <>
            <DialogHeader>
              <DialogTitle>Excluir usuário?</DialogTitle>
            </DialogHeader>
            <DialogContent>
              <p className="text-sm text-ink-2 dark:text-ink-dark-2">
                Tem certeza que deseja excluir <strong>{confirmDelete.nome}</strong>? Essa ação
                não pode ser desfeita — o usuário perde acesso à plataforma imediatamente.
              </p>
            </DialogContent>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </Button>
              <Button
                variant="destructive"
                loading={pending === confirmDelete.id}
                onClick={handleConfirmDelete}
              >
                Excluir
              </Button>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </section>
  );
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Nunca acessou';
  const date = new Date(iso);
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'Agora há pouco';
  if (minutes < 60) return `Há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Há ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Há ${days} ${days === 1 ? 'dia' : 'dias'}`;
  return formatDate(date);
}
